import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Checkbox,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  OpenInNew as ButlerIcon,
  SwapHoriz as MoveIcon
} from '@mui/icons-material';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore';
import { APIProvider } from '@vis.gl/react-google-maps';
import PlaceAutocomplete from '../common/PlaceAutocomplete';
import { useTransportWording } from '../../utils/transportWording';
import { db } from '../../firebase/config';

/**
 * Bestellorte anlegen, bearbeiten, loeschen — gemeinsam genutzt vom
 * Einrichtungs-Assistenten (Schritt 4) und von der Einstellungs-Sektion, damit
 * ein Bestellort auch ohne Durchlauf des Assistenten verwaltet werden kann.
 * Konzept: docs/general/venue_bestellknopf_taxibutler_konzept.md (§10.7)
 *
 * Die Liste der Bestellorte kommt von aussen: Beide Seiten hoeren ohnehin schon
 * auf die Collection, ein zweiter Listener waere doppelte Arbeit.
 */

// Fahrzeugarten des Bestellknopfs. Als Funktion, weil die Bezeichnung des
// Standardfahrzeugs an der Unternehmensform haengt (Taxi bzw. Wagen) —
// nextcab_mobile/docs/general/unternehmensform_konzept.md
export const vehicleTypeOptions = (wording) => [
  { value: 1, label: wording.standardVehicle },
  { value: 2, label: 'Großraum' },
  { value: 3, label: 'Kleinbus (7 Sitze)' },
  { value: 4, label: 'Kleinbus (8 Sitze)' }
];

const emptyVenueForm = {
  id: null,
  // Vorhandene Kundenreferenz aus dem Taxi-Butler-Portal. Leer lassen erzeugt eine neue.
  customId: '',
  name: '',
  address: '',
  latitude: null,
  longitude: null,
  phone: '',
  operatorId: '',
  driverNote: '',
  defaultVehicleType: 1,
  butlerSerialNos: '',
  carTypeRows: [{ identifier: '', vehicleType: 1 }],
  active: true
};

/** Ein Wert mit Kopieren-Knopf; Monospace, weil alles davon abgetippt wird. */
const CopyValue = ({ value }) => {
  if (value === null || value === undefined || value === '') return <span>–</span>;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
        {value}
      </Typography>
      <IconButton size="small" onClick={() => navigator.clipboard?.writeText(`${value}`)}>
        <CopyIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Stack>
  );
};

/**
 * Was im Taxi-Butler-Portal einzutragen ist, in der Reihenfolge des dortigen
 * Formulars. Der Bestellort muss bei uns zuerst existieren, denn erst dann gibt
 * es die Kundenreferenz — deshalb erscheint dieses Fenster direkt nach dem Anlegen und
 * ist ueber die Zeile jederzeit wieder erreichbar.
 */
/**
 * Geraetemodelle fuer die Handreichung. Das Formular im Taxi-Butler-Portal ist fuer
 * alle gleich; PLUS und KIOSK blenden darunter aber einen zweiten Block ein, in dem
 * Fahrzeugart, Zahlart, Wunschzeit und Ziel konfiguriert werden. Wer das uebersieht,
 * bekommt ein Geraet, das nur den einen Knopf kann — die Auswahl hier entscheidet
 * deshalb, welche Felder die Handreichung ueberhaupt nennt.
 */
const HANDOVER_DEVICES = [
  { value: 'one', label: 'Butler ONE', plusOptions: false },
  { value: 'plus6', label: 'Butler PLUS 6', plusOptions: true },
  { value: 'kiosk', label: 'KIOSK', plusOptions: true }
];

const ButlerHandoverDialog = ({ venue, open, onClose, onConfirm, busy }) => {
  const wording = useTransportWording();
  const VEHICLE_TYPE_OPTIONS = vehicleTypeOptions(wording);
  const [showManual, setShowManual] = useState(false);
  const [deviceModel, setDeviceModel] = useState('plus6');
  if (!venue) return null;
  const serials = (venue.butlerSerialNos || []).join(', ');
  const device = HANDOVER_DEVICES.find((d) => d.value === deviceModel) || HANDOVER_DEVICES[0];
  const carTypes = Object.entries(venue.carTypeMap || {});
  const defaultVehicleLabel =
    VEHICLE_TYPE_OPTIONS.find((o) => o.value === (Number(venue.defaultVehicleType) || 1))?.label
    || wording.standardVehicle;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Im Taxi-Butler-Portal ergänzen</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>{venue.name}</strong> ist bei uns angelegt. Legen Sie ihn im
          Taxi-Butler-Portal unter „Abholort hinzufügen“ an: Namen eintippen und aus
          den Vorschlägen wählen — Adresse, Koordinaten, Telefon und Zeitzone füllt
          Taxi Butler damit selbst. Was danach von Hand einzutragen ist, hängt am
          Gerät:
        </Typography>

        {/*
          Welches Geraet hier haengt, weiss nur der Betreiber — wir sehen die
          Seriennummer, aber nicht das Modell. Die Auswahl steuert allein, welche
          Felder unten stehen; gespeichert wird sie nicht, denn sie beschreibt einen
          einmaligen Handgriff im fremden Portal und nichts an unserem Bestellort.
        */}
        <TextField
          select
          size="small"
          fullWidth
          label="Gerät an diesem Bestellort"
          value={deviceModel}
          onChange={(e) => setDeviceModel(e.target.value)}
          sx={{ mb: 2 }}
        >
          {HANDOVER_DEVICES.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>

        <Box sx={{ p: 1.5, mb: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Kundenreferenz
          </Typography>
          <CopyValue value={venue.id} />
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Das Feld steht am Ende des Formulars unter „Maximum gleichzeitiger
          Bestellungen“.
        </Typography>

        {!device.plusOptions && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Für den ONE ist nichts weiter einzustellen. Er bestellt auf einen Druck,
            ohne Ziel und ohne Wunschzeit — wir disponieren dann{' '}
            <strong>{defaultVehicleLabel}</strong> (Standard-Fahrzeugtyp dieses
            Bestellorts).
          </Alert>
        )}

        {device.plusOptions && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Zusätzlich im Abschnitt „Taxi Butler PLUS Options“
            </Typography>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Dieser Abschnitt bleibt leer, solange dort <strong>„Enabled“ auf JA</strong>
              {' '}steht — erst dann erscheinen die Felder darunter.
            </Alert>

            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Fahrzeugart.</strong> Beschriftung und Beschreibung sind freier
              Text für das Display. Entscheidend ist das Konfigurationsfeld{' '}
              <em>vehicleType</em>: Dort muss genau der Bezeichner stehen, den dieser
              Bestellort kennt — sonst fällt die Bestellung auf den Standard zurück.
            </Typography>
            {carTypes.length > 0 ? (
              <Table size="small" sx={{ mb: 1.5 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Feld „vehicleType“</TableCell>
                    <TableCell>Wir disponieren dann</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {carTypes.map(([identifier, vehicleType]) => (
                    <TableRow key={identifier}>
                      <TableCell><CopyValue value={identifier} /></TableCell>
                      <TableCell>
                        {VEHICLE_TYPE_OPTIONS.find((o) => o.value === Number(vehicleType))?.label
                          || `Fahrzeugtyp ${vehicleType}`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                Für diesen Bestellort ist keine Fahrzeugart hinterlegt. Lassen Sie den
                Abschnitt im Taxi-Butler-Portal weg — oder tragen Sie die Zuordnung
                zuerst hier ein (Bestellort bearbeiten, „Fahrzeugart“). Ohne sie
                landet jede Bestellung auf <strong>{defaultVehicleLabel}</strong>.
              </Alert>
            )}

            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Payment Type</TableCell>
                  <TableCell>
                    Kann leer bleiben. Die Geräte fragen die Zahlart bisher nicht ab,
                    und wir werten sie nicht aus — bezahlt wird im Fahrzeug.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Gewünschte Uhrzeit</TableCell>
                  <TableCell>
                    Sichtbar, wenn das Haus vorbestellen können soll. Die Zeit kommt
                    bei uns als Vorbestellung an, frühester und spätester Termin sind
                    Ihre Entscheidung.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Destination</TableCell>
                  <TableCell>
                    Sichtbar, „Preset destination“ auf NEIN. Das Gerät schickt dann
                    Zieladresse samt Koordinaten — der Fahrer sieht die Strecke schon
                    im Angebot.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Passenger Name / Phone</TableCell>
                  <TableCell>
                    Sichtbar. Der Name erscheint dem Fahrer als Fahrgast, die
                    Nummer als dessen Kontakt. Bleiben beide leer, treten der Name
                    und die Telefonnummer des Bestellorts an ihre Stelle.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        )}

        {/*
          Das Ankoppeln steht bewusst hinter allen Angaben: Es ist der letzte
          Handgriff, und im Taxi-Butler-Portal folgt es ebenfalls erst auf das
          Speichern des Abholorts.
        */}
        <Alert severity="info" sx={{ mb: 2 }}>
          Nach dem Speichern bietet Taxi Butler das Ankoppeln des Geräts an:
          verlangt wird der Registrierungsschlüssel (XX-XX-XX), den nur das Display
          des eingeschalteten Geräts zeigt.
        </Alert>

        {/*
          Der Normalfall braucht nur die Kundenreferenz — alles Weitere fuellt die
          Adresssuche im Taxi-Butler-Portal. Fuer den Ausnahmefall, dass eine
          Adresse dort nicht gefunden wird, stehen die Werte trotzdem bereit, nur
          eben eingeklappt.
        */}
        <Button size="small" onClick={() => setShowManual((v) => !v)}>
          {showManual ? 'Angaben ausblenden' : 'Adresse nicht in den Vorschlägen?'}
        </Button>

        <Collapse in={showManual}>
          <Typography variant="body2" sx={{ mt: 1.5, mb: 1 }}>
            Dann die Felder von Hand füllen. „RECALCULATE COORDINATES“ im
            Taxi-Butler-Formular rechnet die Koordinaten aus der eingetippten Adresse
            neu aus — meist der schnellere Weg als das Abtippen der Zahlen.
          </Typography>
          <Table size="small" sx={{ mb: 1 }}>
            <TableBody>
              <TableRow>
                <TableCell>Name Abholort</TableCell>
                <TableCell><CopyValue value={venue.name} /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Adresse</TableCell>
                <TableCell><CopyValue value={venue.address} /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Telefonnummer</TableCell>
                <TableCell><CopyValue value={venue.phone} /></TableCell>
              </TableRow>
              {/*
                Die Zeilen tragen die Beschriftung des Taxi-Butler-Formulars, nicht
                die richtige: Dort ist das erste Koordinatenfeld "Laengengrad"
                beschriftet, nimmt aber die geografische Breite. Wer nach der
                Beschriftung geht, vertauscht beide Werte. Von oben nach unten
                abtippen fuehrt dagegen zum richtigen Punkt.
              */}
              <TableRow>
                <TableCell>Feld „Längengrad“</TableCell>
                <TableCell><CopyValue value={venue.latitude} /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Feld „Breitengrad“</TableCell>
                <TableCell><CopyValue value={venue.longitude} /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Seriennummer des Geräts</TableCell>
                <TableCell><CopyValue value={serials} /></TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Die beiden Koordinatenfelder sind im Taxi-Butler-Portal vertauscht
            beschriftet — das Feld „Längengrad“ nimmt dort die geografische
            <strong> Breite</strong>. Die Tabelle ist bereits so sortiert: von oben
            nach unten abtippen, nicht nach der Beschriftung gehen.
          </Alert>
          <Alert severity="info">
            Bleibt die Kundenreferenz leer, ordnen wir über die Seriennummer zu und
            zuletzt über die Koordinaten (150 Meter Umkreis). Einer der drei Wege
            muss stimmen, sonst weisen wir die Bestellung ab.
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Später</Button>
        <Button variant="contained" onClick={() => onConfirm(venue.id)} disabled={busy}>
          Im Taxi-Butler-Portal eingetragen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const VenueManager = ({ venues, organizationId, onError }) => {
  const wording = useTransportWording();
  const VEHICLE_TYPE_OPTIONS = vehicleTypeOptions(wording);
  const [operators, setOperators] = useState([]);
  const [venueDialogOpen, setVenueDialogOpen] = useState(false);
  const [venueForm, setVenueForm] = useState(emptyVenueForm);
  const [busy, setBusy] = useState(false);
  // Bestellort, dessen Angaben fuer das Taxi-Butler-Portal gerade gezeigt werden.
  const [handoverVenueId, setHandoverVenueId] = useState(null);
  // Geraet, das gerade an einen anderen Bestellort umzieht: { venueId, serial }.
  const [moveDevice, setMoveDevice] = useState(null);
  const [moveTargetId, setMoveTargetId] = useState('');
  const [moveSwap, setMoveSwap] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, 'operators'), where('active', '==', true)),
      (snapshot) => setOperators(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => onError?.(`Unternehmer konnten nicht geladen werden: ${err?.message || 'unbekannter Fehler'}`)
    );
    return unsubscribe;
  }, [onError]);

  const handoverVenue = venues.find((v) => v.id === handoverVenueId) || null;

  const openVenueDialog = (venue = null) => {
    if (venue) {
      const carTypeRows = Object.entries(venue.carTypeMap || {}).map(([identifier, vehicleType]) => ({
        identifier,
        vehicleType: Number(vehicleType) || 1
      }));
      setVenueForm({
        id: venue.id,
        customId: '',
        name: venue.name || '',
        address: venue.address || '',
        latitude: venue.latitude ?? null,
        longitude: venue.longitude ?? null,
        phone: venue.phone || '',
        operatorId: venue.operatorId || '',
        driverNote: venue.driverNote || '',
        defaultVehicleType: Number(venue.defaultVehicleType) || 1,
        butlerSerialNos: (venue.butlerSerialNos || []).join(', '),
        carTypeRows: carTypeRows.length ? carTypeRows : [{ identifier: '', vehicleType: 1 }],
        active: venue.active !== false
      });
    } else {
      setVenueForm(emptyVenueForm);
    }
    setVenueDialogOpen(true);
  };

  const handleSaveVenue = async () => {
    setBusy(true);
    onError?.(null);
    try {
      const carTypeMap = {};
      venueForm.carTypeRows.forEach((row) => {
        const key = row.identifier.trim().toLowerCase();
        if (key) carTypeMap[key] = Number(row.vehicleType) || 1;
      });
      const serials = venueForm.butlerSerialNos
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);

      // Eine Seriennummer an zwei Bestellorten ist nicht aufloesbar: Die Zuordnung
      // ueber das Geraet nimmt den ersten Treffer, und welcher das ist, entscheidet
      // Firestore. Lieber hier abbrechen als spaeter Fahrten am falschen Ort.
      const clash = serials
        .map((serial) => ({
          serial,
          venue: venues.find(
            (v) => v.id !== venueForm.id && (v.butlerSerialNos || []).includes(serial)
          )
        }))
        .find((entry) => entry.venue);
      if (clash) {
        throw new Error(
          `Die Seriennummer "${clash.serial}" ist bereits bei "${clash.venue.name}" `
          + 'hinterlegt. Verschieben Sie das Gerät dort über das Geräte-Kennzeichen, '
          + 'statt die Nummer ein zweites Mal einzutragen.'
        );
      }

      const payload = {
        name: venueForm.name.trim(),
        address: venueForm.address.trim(),
        latitude: venueForm.latitude,
        longitude: venueForm.longitude,
        phone: venueForm.phone.trim() || null,
        operatorId: venueForm.operatorId || null,
        driverNote: venueForm.driverNote.trim() || null,
        defaultVehicleType: Number(venueForm.defaultVehicleType) || 1,
        butlerSerialNos: serials,
        carTypeMap,
        active: venueForm.active,
        organizationId
      };
      if (venueForm.id) {
        // Zieht ein Bestellort um, stimmt der Abholort im Taxi-Butler-Portal nicht
        // mehr — Anzeige am Geraet und der Koordinaten-Fallback haengen dort an
        // einer eigenen Kopie. Die Zeile faellt deshalb auf "ergaenzen" zurueck.
        const before = venues.find((v) => v.id === venueForm.id);
        const moved =
          before &&
          (before.address !== payload.address ||
            before.latitude !== payload.latitude ||
            before.longitude !== payload.longitude);
        await updateDoc(doc(db, 'venues', venueForm.id), {
          ...payload,
          ...(moved ? { butlerLinkedAt: null } : {})
        });
        setVenueDialogOpen(false);
        if (moved) setHandoverVenueId(venueForm.id);
      } else if (venueForm.customId.trim()) {
        // Bestehende Taxi-Butler-Kunden übernehmen ihre vorhandene
        // customerReference als Dokument-ID — dann muss an den Geräten nichts
        // umkonfiguriert werden.
        const customId = venueForm.customId.trim();
        if (venues.some((v) => v.id === customId)) {
          throw new Error(`Die Kundenreferenz "${customId}" ist bereits vergeben.`);
        }
        await setDoc(doc(db, 'venues', customId), payload);
        setVenueDialogOpen(false);
        // Kundenreferenz kam aus dem Taxi-Butler-Portal, der Abholort existiert dort
        // also bereits — dann ist nichts zu ergaenzen.
        await updateDoc(doc(db, 'venues', customId), { butlerLinkedAt: serverTimestamp() });
      } else {
        const ref = await addDoc(collection(db, 'venues'), payload);
        setVenueDialogOpen(false);
        setHandoverVenueId(ref.id);
      }
    } catch (err) {
      onError?.(err?.message || 'Bestellort konnte nicht gespeichert werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteVenue = async (venueId) => {
    try {
      await deleteDoc(doc(db, 'venues', venueId));
    } catch (err) {
      onError?.(err?.message || 'Bestellort konnte nicht gelöscht werden.');
    }
  };

  /**
   * Ob ein Abholort im Taxi-Butler-Portal existiert, koennen wir von hier aus
   * nicht pruefen — Taxi Butler ruft uns an, nicht umgekehrt. Deshalb bestaetigt
   * es der Betreiber selbst, und bis dahin bleibt die Zeile als offen markiert.
   */
  const handleConfirmLinked = async (venueId) => {
    setBusy(true);
    try {
      await updateDoc(doc(db, 'venues', venueId), { butlerLinkedAt: serverTimestamp() });
      setHandoverVenueId(null);
    } catch (err) {
      onError?.(err?.message || 'Bestellort konnte nicht aktualisiert werden.');
    } finally {
      setBusy(false);
    }
  };

  const moveSource = moveDevice ? venues.find((v) => v.id === moveDevice.venueId) : null;
  const moveTarget = moveTargetId ? venues.find((v) => v.id === moveTargetId) : null;
  const moveTargetDevices = (moveTarget?.butlerSerialNos || []).filter(
    (serial) => serial !== moveDevice?.serial
  );

  const openMoveDialog = (venue, serial) => {
    setMoveDevice({ venueId: venue.id, serial });
    setMoveTargetId('');
    setMoveSwap(false);
  };

  /**
   * Ein Geraet haengt immer an genau einem Bestellort — zieht es um, muss die
   * Seriennummer beim alten weg. Beide Seiten in einem Batch, damit die Nummer
   * nie gleichzeitig an zwei Orten steht.
   *
   * Beide Bestellorte fallen dabei auf "ergaenzen" zurueck: Welches Geraet an
   * welchem Abholort haengt, entscheidet das Taxi-Butler-Portal, und ob es dort
   * schon passt, koennen wir von hier aus nicht sehen.
   */
  const handleMoveDevice = async () => {
    if (!moveSource || !moveTarget) return;
    setBusy(true);
    try {
      const { serial } = moveDevice;
      const remaining = (moveSource.butlerSerialNos || []).filter((s) => s !== serial);
      const batch = writeBatch(db);
      batch.update(doc(db, 'venues', moveSource.id), {
        butlerSerialNos: moveSwap ? [...remaining, ...moveTargetDevices] : remaining,
        butlerLinkedAt: null
      });
      batch.update(doc(db, 'venues', moveTarget.id), {
        butlerSerialNos: moveSwap ? [serial] : [...moveTargetDevices, serial],
        butlerLinkedAt: null
      });
      await batch.commit();
      setMoveDevice(null);
    } catch (err) {
      onError?.(err?.message || 'Das Gerät konnte nicht verschoben werden.');
    } finally {
      setBusy(false);
    }
  };

  const pendingCount = venues.filter((venue) => !venue.butlerLinkedAt).length;

  return (
    <Box>
      {pendingCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {pendingCount === 1
            ? 'Ein Bestellort ist im Taxi-Butler-Portal noch nicht ergänzt.'
            : `${pendingCount} Bestellorte sind im Taxi-Butler-Portal noch nicht ergänzt.`}
          {' '}Von dort kommt keine Bestellung an, solange der Abholort dort fehlt.
          Die nötigen Angaben stehen hinter dem Knopf in der jeweiligen Zeile.
        </Alert>
      )}

      {venues.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Noch kein Bestellort angelegt.
        </Alert>
      ) : (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Bestellort</TableCell>
              <TableCell>Kundenreferenz</TableCell>
              <TableCell>Taxi-Butler-Portal</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {venues.map((venue) => (
              <TableRow key={venue.id}>
                <TableCell>
                  <strong>{venue.name}</strong>
                  {venue.active === false && (
                    <Chip size="small" label="inaktiv" sx={{ ml: 1 }} />
                  )}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {venue.address}
                  </Typography>
                  {/* Geraete-Kennzeichen: anklicken heisst "zieht woanders hin". */}
                  {(venue.butlerSerialNos || []).map((serial) => (
                    <Tooltip key={serial} title="Gerät an einen anderen Bestellort verschieben">
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<MoveIcon />}
                        label={serial}
                        onClick={() => openMoveDialog(venue, serial)}
                        sx={{ mt: 0.5, mr: 0.5, fontFamily: 'monospace' }}
                      />
                    </Tooltip>
                  ))}
                </TableCell>
                <TableCell>
                  <CopyValue value={venue.id} />
                </TableCell>
                <TableCell>
                  {venue.butlerLinkedAt ? (
                    <Chip size="small" color="success" variant="outlined" label="eingetragen" />
                  ) : (
                    <Chip
                      size="small"
                      color="warning"
                      label="ergänzen"
                      onClick={() => setHandoverVenueId(venue.id)}
                    />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Angaben für das Taxi-Butler-Portal">
                    <IconButton size="small" onClick={() => setHandoverVenueId(venue.id)}>
                      <ButlerIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => openVenueDialog(venue)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteVenue(venue.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Button startIcon={<AddIcon />} variant="outlined" onClick={() => openVenueDialog()}>
        Bestellort hinzufügen
      </Button>

      <Dialog open={Boolean(moveDevice)} onClose={() => setMoveDevice(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Gerät verschieben</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Das Gerät <strong>{moveDevice?.serial}</strong> ist bei
            {' '}<strong>{moveSource?.name}</strong> hinterlegt.
          </Typography>
          <TextField
            select
            label="Neuer Bestellort"
            value={moveTargetId}
            onChange={(e) => {
              setMoveTargetId(e.target.value);
              setMoveSwap(false);
            }}
            size="small"
            fullWidth
          >
            {venues
              .filter((venue) => venue.id !== moveDevice?.venueId)
              .map((venue) => (
                <MenuItem key={venue.id} value={venue.id}>
                  {venue.name}
                </MenuItem>
              ))}
          </TextField>

          {moveTargetDevices.length > 0 && (
            <>
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox checked={moveSwap} onChange={(e) => setMoveSwap(e.target.checked)} />
                }
                label="Geräte tauschen"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {moveSwap
                  ? `${moveTargetDevices.join(', ')} kommt im Gegenzug zu ${moveSource?.name}.`
                  : `${moveTarget?.name} hat bereits ${moveTargetDevices.join(', ')} — ohne Tausch stehen dort danach beide Geräte.`}
              </Typography>
            </>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            Beide Bestellorte werden danach wieder als „ergänzen“ geführt: Welches
            Gerät an welchem Abholort hängt, entscheidet das Taxi-Butler-Portal —
            ob es dort schon passt, sehen wir von hier aus nicht.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDevice(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleMoveDevice} disabled={busy || !moveTargetId}>
            Verschieben
          </Button>
        </DialogActions>
      </Dialog>

      <ButlerHandoverDialog
        venue={handoverVenue}
        open={Boolean(handoverVenue)}
        onClose={() => setHandoverVenueId(null)}
        onConfirm={handleConfirmLinked}
        busy={busy}
      />

      {/* Bestellort anlegen/bearbeiten. Der APIProvider ist Voraussetzung fuer die
          Adress-Vorschlaege — PlaceAutocomplete holt sich die Places-Bibliothek ueber
          useMapsLibrary und bleibt ohne ihn stumm. Er haengt am geoeffneten Dialog,
          damit die Maps-Bibliothek nicht auf jedem Aufruf der Einstellungen laedt. */}
      {venueDialogOpen && (
      <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
        <Dialog open={venueDialogOpen} onClose={() => setVenueDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{venueForm.id ? 'Bestellort bearbeiten' : 'Bestellort hinzufügen'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Name *"
                value={venueForm.name}
                onChange={(e) => setVenueForm((f) => ({ ...f, name: e.target.value }))}
                size="small"
                fullWidth
                placeholder="Hotel Stadtpark"
              />
              {!venueForm.id && (
                <TextField
                  label="Vorhandene Kundenreferenz aus dem Taxi-Butler-Portal"
                  value={venueForm.customId}
                  onChange={(e) => setVenueForm((f) => ({ ...f, customId: e.target.value }))}
                  size="small"
                  fullWidth
                  helperText={
                    'Nur ausfüllen, wenn der Bestellort bei Taxi Butler schon existiert — dann '
                    + 'muss am Gerät nichts geändert werden. Sonst leer lassen, wir vergeben eine.'
                  }
                />
              )}
              <PlaceAutocomplete
                label="Abholadresse *"
                value={venueForm.address}
                onChange={(e) =>
                  setVenueForm((f) => ({ ...f, address: e.target.value, latitude: null, longitude: null }))
                }
                onPlaceSelect={(address, location) =>
                  setVenueForm((f) => ({
                    ...f,
                    address,
                    latitude: location?.latitude ?? null,
                    longitude: location?.longitude ?? null
                  }))
                }
                required
                size="small"
                fullWidth
              />
              {venueForm.address && venueForm.latitude === null && (
                <Alert severity="warning">
                  Bitte die Adresse aus den Vorschlägen auswählen — ohne Koordinaten kann
                  der Bestellort nicht disponiert werden.
                </Alert>
              )}
              <TextField
                label="Telefon des Bestellorts"
                value={venueForm.phone}
                onChange={(e) => setVenueForm((f) => ({ ...f, phone: e.target.value }))}
                size="small"
                fullWidth
              />
              <TextField
                select
                label="Unternehmer"
                value={venueForm.operatorId}
                onChange={(e) => setVenueForm((f) => ({ ...f, operatorId: e.target.value }))}
                size="small"
                fullWidth
                helperText="Leer lassen, wenn die Zentrale disponiert"
              >
                <MenuItem value="">Zentrale</MenuItem>
                {operators.map((operator) => (
                  <MenuItem key={operator.id} value={operator.id}>
                    {operator.name || operator.companyName || operator.id}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Hinweis für den Fahrer"
                value={venueForm.driverNote}
                onChange={(e) => setVenueForm((f) => ({ ...f, driverNote: e.target.value }))}
                size="small"
                fullWidth
                placeholder="An der Rezeption klingeln"
              />
              <TextField
                select
                label="Fahrzeugtyp als Standard"
                value={venueForm.defaultVehicleType}
                onChange={(e) => setVenueForm((f) => ({ ...f, defaultVehicleType: e.target.value }))}
                size="small"
                fullWidth
              >
                {VEHICLE_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Geräte-Seriennummern"
                value={venueForm.butlerSerialNos}
                onChange={(e) => setVenueForm((f) => ({ ...f, butlerSerialNos: e.target.value }))}
                size="small"
                fullWidth
                helperText="Mehrere durch Komma trennen — steht auf der Rückseite des Geräts"
              />

              <Divider />
              <Box>
                <Typography variant="subtitle2">Fahrzeugarten des Geräts</Typography>
                <Typography variant="caption" color="text.secondary">
                  Bei Geräten mit Bildschirm wählt der Gast eine Fahrzeugart. Welche
                  Fahrzeugart-Kennung dabei übermittelt wird, legt Taxi Butler je Bestellort fest —
                  tragen Sie sie hier ein und ordnen Sie Ihren Fahrzeugtyp zu.
                </Typography>
              </Box>
              {venueForm.carTypeRows.map((row, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <TextField
                    label="Fahrzeugart-Kennung"
                    value={row.identifier}
                    onChange={(e) =>
                      setVenueForm((f) => {
                        const rows = [...f.carTypeRows];
                        rows[index] = { ...rows[index], identifier: e.target.value };
                        return { ...f, carTypeRows: rows };
                      })
                    }
                    size="small"
                    placeholder="regular"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    select
                    label="Fahrzeugtyp"
                    value={row.vehicleType}
                    onChange={(e) =>
                      setVenueForm((f) => {
                        const rows = [...f.carTypeRows];
                        rows[index] = { ...rows[index], vehicleType: e.target.value };
                        return { ...f, carTypeRows: rows };
                      })
                    }
                    size="small"
                    sx={{ flex: 1 }}
                  >
                    {VEHICLE_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setVenueForm((f) => ({
                        ...f,
                        carTypeRows: f.carTypeRows.filter((_, i) => i !== index)
                      }))
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  setVenueForm((f) => ({
                    ...f,
                    carTypeRows: [...f.carTypeRows, { identifier: '', vehicleType: 1 }]
                  }))
                }
              >
                Fahrzeugart ergänzen
              </Button>

              <FormControlLabel
                control={
                  <Switch
                    checked={venueForm.active}
                    onChange={(e) => setVenueForm((f) => ({ ...f, active: e.target.checked }))}
                  />
                }
                label="Bestellort aktiv"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVenueDialogOpen(false)}>Abbrechen</Button>
            <Button
              variant="contained"
              onClick={handleSaveVenue}
              disabled={
                busy ||
                !venueForm.name.trim() ||
                !venueForm.address.trim() ||
                venueForm.latitude === null
              }
            >
              Speichern
            </Button>
          </DialogActions>
        </Dialog>
      </APIProvider>
      )}
    </Box>
  );
};

export default VenueManager;
